import { demoFunc } from './dees-harness-message-list.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
  directives,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import type {
  IHarnessMessage,
  IHarnessMessageViewState,
  IHarnessPermissionRequest,
  IHarnessPermissionViewState,
  IHarnessQuestionRequest,
  IHarnessQuestionViewState,
  IHarnessStatus,
  IHarnessTranscriptViewState,
  THarnessMessageStreamDelta,
  THarnessStreamDelta,
} from '../interfaces.js';
import { DeesHarnessMessage } from '../dees-harness-message/dees-harness-message.js';
import { DeesHarnessPermissionCard } from '../dees-harness-permission-card/dees-harness-permission-card.js';
import { DeesHarnessToolRegistry } from '../toolregistry.js';
import { harnessApplyMessageDelta, harnessRendersToolRow } from '../harness.helpers.js';
import '../dees-harness-status/dees-harness-status.js';
import '../dees-harness-question-card/dees-harness-question-card.js';
import '../../00group-utility/dees-icon/dees-icon.js';

const { repeat } = directives;

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-message-list': DeesHarnessMessageList;
  }
}

const pinThresholdPx = 48;
const loadEarlierThresholdPx = 24;
const scrollIntentSettlementMs = 250;
const virtualizationThreshold = 40;
const virtualOverscanPx = 800;
const virtualGapPx = 8;
const toolRowHeightPx = 28;
const toolRowGapPx = 2;

type TMessageTimelineItem = {
  kind: 'message';
  time: number;
  insertionIndex: number;
  key: string;
  message: IHarnessMessage;
  /** The message is nothing but a tool call, so it renders as one row of the transcript. */
  toolRow: boolean;
};

type TTimelineItem =
  | TMessageTimelineItem
  | {
      kind: 'permission';
      time: number;
      insertionIndex: number;
      key: string;
      request: IHarnessPermissionRequest;
    }
  | {
      kind: 'question';
      time: number;
      insertionIndex: number;
      key: string;
      question: IHarnessQuestionRequest;
    };

/**
 * Scrollable, streaming-safe message log. Short transcripts keep stable keyed
 * DOM; long transcripts mount a bounded, measured window and retain row state
 * by id. `applyDelta()` targets a mounted row or updates its offscreen model.
 * Auto-follow stays at the bottom until the user intentionally scrolls away.
 */
@customElement('dees-harness-message-list')
export class DeesHarnessMessageList extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property({ attribute: false })
  accessor messages: IHarnessMessage[] = [];

  @property({ attribute: false })
  accessor status: IHarnessStatus = { type: 'idle' };

  /** permission requests interleave chronologically with the messages;
   * answered ones stay visible showing their outcome */
  @property({ attribute: false })
  accessor permissions: IHarnessPermissionRequest[] = [];

  @property({ type: Boolean })
  accessor permissionsBusy: boolean = false;

  /** agent questions interleave like permissions; answered ones persist */
  @property({ attribute: false })
  accessor questions: IHarnessQuestionRequest[] = [];

  @property({ type: Boolean })
  accessor autoFollow: boolean = true;

  /** Stable host-owned identity for one transcript and its scroll context. */
  @property()
  accessor transcriptKey: string = '';

  @property({ type: Boolean })
  accessor hasEarlierMessages: boolean = false;

  @property({ type: Boolean })
  accessor loadingEarlier: boolean = false;

  @property({ type: Boolean })
  accessor hideScrollbar: boolean = false;

  @property({ type: Boolean })
  accessor fadeScrollEdges: boolean = false;

  @property({ attribute: false })
  accessor toolRegistry: DeesHarnessToolRegistry = DeesHarnessToolRegistry.default;

  @property({ attribute: false })
  accessor viewState: IHarnessTranscriptViewState | undefined = undefined;

  /** Parse markdown during active streams; disable to parse only at message end. */
  @property({ type: Boolean })
  accessor markdownWhileStreaming: boolean = true;

  @property({ type: Boolean })
  accessor showRoles: boolean = true;

  @property({ type: Boolean })
  accessor showTimestamps: boolean = false;

  @property()
  accessor emptyTitle: string = 'No messages yet';

  @property()
  accessor emptyText: string = 'Start a conversation to see it here.';

  @state()
  accessor showJumpChip: boolean = false;

  @state()
  accessor scrollFadeTop: boolean = false;

  @state()
  accessor scrollFadeBottom: boolean = false;

  private timelineRenderItems: TTimelineItem[] = [];
  private timelineRegistryRevision = -1;
  private readonly knownTimelineKeys = new Set<string>();
  private readonly enteringKeys = new Set<string>();
  private timelineBuilt = false;
  private readonly localViewState: IHarnessTranscriptViewState = {
    messages: {},
    permissions: {},
    questions: {},
  };
  private readonly measuredVirtualHeights = new Map<string, number>();
  private timelineIndexByKey = new Map<string, number>();
  private virtualOffsets: number[] = [];
  private virtualTotalHeight = 0;
  private virtualStartIndex = 0;
  private virtualEndIndex = 0;
  private retainedVirtualKeys = new Set<string>();
  private selectionGestureKeys = new Set<string>();
  private selectionGestureActive = false;
  private selectionGestureStartOwner: Element | undefined;
  private selectionGestureCrossesOwners = false;
  private selectionGestureSnapshot: {
    anchorNode: Node | null;
    anchorOffset: number;
    focusNode: Node | null;
    focusOffset: number;
    rangeCount: number;
  } | undefined;
  private explicitRetainedVirtualKey: string | undefined;
  private virtualRowResizeObserver: ResizeObserver | null = null;
  private virtualViewportResizeObserver: ResizeObserver | null = null;
  private virtualMeasureFrame: number | null = null;
  private virtualRangeFrame: number | null = null;
  private lastVirtualViewportWidth = 0;
  private pendingVirtualAnchor: { key: string; offset: number } | undefined;
  private pendingFullAnchor: { key: string; offset: number } | undefined;
  private activeFullAnchor: { key: string; offset: number } | undefined;
  private fullAnchorRestoreGeneration = 0;
  private fullAnchorRestoreFrame: number | null = null;
  private fullAnchorRestoreSecondFrame: number | null = null;
  private measurementAnchor: { key: string; offset: number } | undefined;
  private pendingVirtualRepin = false;
  private virtualizationEnabled = false;
  private virtualizationEnablePending = false;
  private virtualizationDisablePending = false;
  private virtualizationActivationFrame: number | null = null;
  private selectionChangeListening = false;
  private loadEarlierLatched = false;
  private loadingEarlierObserved = false;
  private earlierScrollIntent = false;
  private scrollbarGestureActive = false;
  private touchScrollY: number | undefined;
  private scrollIntentTimer: ReturnType<typeof setTimeout> | undefined;
  private programmaticScrollTop: number | undefined;
  private programmaticScrollGeneration = 0;
  private programmaticScrollFrame: number | null = null;
  private programmaticScrollSecondFrame: number | null = null;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        position: relative;
        display: block;
        min-height: 0;
        min-width: 0;
        max-width: 100%;
        font-family: var(--dees-font-family);
      }

      .scroll {
        height: 100%;
        overflow-y: auto;
        overflow-anchor: none;
        overscroll-behavior: contain;
        scrollbar-width: thin;
        scrollbar-color: var(--dees-color-scrollbar-thumb) transparent;
        padding: 16px;
        box-sizing: border-box;
        container: harness-transcript / inline-size;
      }

      .scroll.hideScrollbar {
        scrollbar-width: none;
      }

      .scroll.hideScrollbar::-webkit-scrollbar {
        display: none;
      }

      .scroll.fadeTop {
        -webkit-mask-image: linear-gradient(to bottom, transparent 0, black 40px, black 100%);
        mask-image: linear-gradient(to bottom, transparent 0, black 40px, black 100%);
      }

      .scroll.fadeBottom {
        -webkit-mask-image: linear-gradient(to bottom, black 0, black calc(100% - 40px), transparent 100%);
        mask-image: linear-gradient(to bottom, black 0, black calc(100% - 40px), transparent 100%);
      }

      .scroll.fadeTop.fadeBottom {
        -webkit-mask-image: linear-gradient(
          to bottom,
          transparent 0,
          black 40px,
          black calc(100% - 40px),
          transparent 100%
        );
        mask-image: linear-gradient(
          to bottom,
          transparent 0,
          black 40px,
          black calc(100% - 40px),
          transparent 100%
        );
      }

      /* observed by the follow ResizeObserver; fills the viewport so the
         empty state can center */
      .scrollContent {
        min-height: 100%;
        display: flex;
        flex-direction: column;
      }

      ::slotted([slot='leading']) {
        display: block;
        min-width: 0;
        margin-bottom: var(--dees-spacing-sm);
      }

      .stack {
        /* The two lengths the row spacing is made of, named once: everything below derives from
           them, including how far an arriving row has to start out of the flow. */
        --harness-stack-gap: 12px;
        --harness-row-pull: -10px;
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: var(--harness-stack-gap);
      }

      .stack > * {
        min-width: 0;
        grid-column: 1 / -1;
      }

      .virtualStack {
        position: relative;
        flex: 0 0 auto;
        min-width: 0;
      }

      .virtualItem {
        position: absolute;
        inset: 0 0 auto;
        min-width: 0;
        transform: translate3d(0, var(--harness-virtual-offset), 0);
      }

      /*
       * Prose is held to a readable measure while an opened tool row takes the whole pane. Applied
       * to the transcript rows themselves because a message host clips on both axes, so nothing
       * nested inside one can bleed back out.
       *
       * The measure travels through max-width over a full-width basis rather than through width,
       * so the step interpolates on the same timing as the body disclosure and the row and its
       * body move as one. Declared here, in the tree that owns these rows: the shared default
       * stylesheet applies a universal background/colour transition that reaches an element from
       * its own scope and outranks anything the element declares about itself.
       */
      .stack > *,
      .virtualItem {
        width: 100%;
        max-width: min(var(--dees-harness-content-width, 820px), 100%);
        margin-inline: auto;
        transition:
          max-width var(--dees-transition-default) var(--dees-ease-standard),
          margin var(--dees-transition-default) var(--dees-ease-standard);
      }

      /* Only the row the reader opened takes the pane; its neighbours keep the reading measure. */
      .stack > [data-tool-open],
      .virtualItem[data-tool-open] {
        max-width: 100%;
      }

      /*
       * An opened row steps out of the run it belongs to. The virtual stack says the same with
       * gapAfter(): a margin there would sit outside the measured row height and desync the
       * absolute offsets.
       */
      .stack > [data-tool-open] {
        margin-block: var(--dees-spacing-sm);
      }

      /* Consecutive collapsed tool rows read as one list rather than as separate blocks. */
      .stack > [data-tool-row]:not([data-tool-open]) + [data-tool-row]:not([data-tool-open]) {
        margin-top: var(--harness-row-pull);
      }

      /*
       * A tool row that arrives in a live transcript blends in: opacity only, delayed by the step
       * that makes room for it below when the browser can interpolate to the row's own height.
       */
      .stack > [data-entering],
      .virtualItem[data-entering] {
        animation: harness-row-blend var(--dees-transition-default) var(--dees-ease-standard)
          backwards;
      }

      /*
       * With keyword interpolation the arrival becomes two steps: the list opens the space, and
       * only then does the row blend into it, so the prose below and the followed bottom travel
       * instead of jumping. Without it a height keyframe would step rather than interpolate, so
       * the row is simply there and only the blend runs.
       *
       * The flowed stack has to start without its own spacing, or the step begins with the gap
       * already jolted in: the enter margin cancels the stack gap for that first frame, and the
       * pull of a preceding collapsed row with it. The virtual stack carries the same gap in its
       * measured offsets instead, so there is nothing to cancel there.
       */
      @supports (interpolate-size: allow-keywords) {
        .stack > [data-entering],
        .virtualItem[data-entering] {
          interpolate-size: allow-keywords;
          animation:
            harness-row-space var(--dees-transition-default) var(--dees-ease-standard),
            harness-row-blend var(--dees-transition-default) var(--dees-ease-standard)
              var(--dees-transition-default) backwards;
        }

        .stack > [data-entering] {
          --harness-row-enter-margin: calc(0px - var(--harness-stack-gap));
        }

        .stack > [data-tool-row]:not([data-tool-open]) + [data-entering] {
          --harness-row-enter-margin: calc(var(--harness-row-pull) - var(--harness-stack-gap));
        }
      }

      @keyframes harness-row-space {
        from {
          height: 0;
          margin-top: var(--harness-row-enter-margin, 0px);
        }
      }

      @keyframes harness-row-blend {
        from {
          opacity: 0;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .stack > *,
        .virtualItem {
          transition: none;
        }

        .stack > [data-entering],
        .virtualItem[data-entering] {
          animation: none;
        }
      }

      /*
       * The activity line is the tail of the transcript, not chrome around it, so it sits on the
       * same measure as the prose rows and starts at their left edge instead of spanning the pane.
       */
      .statusRow {
        width: min(var(--dees-harness-content-width, 820px), 100%);
        margin-inline: auto;
      }

      .empty {
        flex: 1;
        display: grid;
        place-content: center;
        gap: var(--dees-spacing-xs);
        min-height: 120px;
        text-align: center;
      }

      .emptyTitle {
        color: var(--dees-color-text-primary);
        font-size: var(--dees-font-control-size, 13px);
        font-weight: 600;
      }

      .emptyText {
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-control-size-sm, 12px);
      }

      .jumpChip {
        position: absolute;
        left: 50%;
        bottom: var(--dees-spacing-md);
        transform: translateX(-50%);
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-xs);
        padding: 4px 12px;
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-full);
        min-height: var(--dees-control-height-md);
        background: var(--dees-color-control-bg);
        color: var(--dees-color-text-primary);
        cursor: pointer;
        font-family: inherit;
        font-size: var(--dees-font-control-size-sm, 12px);
        box-shadow: var(--dees-shadow-md);
      }

      .jumpChip:hover {
        background: var(--dees-color-bg-secondary);
      }

      .jumpChip:focus-visible { outline: 2px solid var(--dees-color-focus-ring); outline-offset: 2px; }

    `,
  ];

  /** Messages and requests merged chronologically (stable ids as keys). */
  private get timelineItems(): TTimelineItem[] {
    const items: TTimelineItem[] = this.messages.map((message, insertionIndex) => ({
      kind: 'message' as const,
      time: message.createdAt || 0,
      insertionIndex,
      key: `message:${message.id}`,
      message,
      toolRow: harnessRendersToolRow(message),
    }));
    for (const request of this.permissions) {
      items.push({
        kind: 'permission' as const,
        // pending requests pin to the end (action required); answered ones
        // freeze at their response time in the flow
        time: request.response
          ? (request.respondedAt ?? request.createdAt ?? 0)
          : Number.MAX_SAFE_INTEGER,
        insertionIndex: items.length,
        key: `permission:${request.id}`,
        request,
      });
    }
    for (const question of this.questions) {
      items.push({
        kind: 'question' as const,
        time: question.response
          ? (question.respondedAt ?? question.createdAt ?? 0)
          : Number.MAX_SAFE_INTEGER,
        insertionIndex: items.length,
        key: `question:${question.id}`,
        question,
      });
    }
    items.sort((left, right) => left.time - right.time || left.insertionIndex - right.insertionIndex);

    // Keep ancillary and legacy-producer chronology fixed, then replace only
    // the slots occupied by explicitly ordered messages. This stays transitive
    // while source order wins even when source timestamps are identical or stale.
    const orderedSlots: number[] = [];
    const orderedMessages: TMessageTimelineItem[] = [];
    for (const [index, item] of items.entries()) {
      if (
        item.kind === 'message'
        && Number.isSafeInteger(item.message.order?.messageIndex)
        && Number(item.message.order?.messageIndex) >= 0
        && Number.isSafeInteger(item.message.order?.partIndex)
        && Number(item.message.order?.partIndex) >= 0
      ) {
        orderedSlots.push(index);
        orderedMessages.push(item);
      }
    }
    orderedMessages.sort((left, right) => (
      left.message.order!.messageIndex - right.message.order!.messageIndex
      || left.message.order!.partIndex - right.message.order!.partIndex
      || left.insertionIndex - right.insertionIndex
    ));
    for (const [index, slot] of orderedSlots.entries()) items[slot] = orderedMessages[index]!;
    return items;
  }

  public willUpdate(changedProperties: Map<PropertyKey, unknown>): void {
    super.willUpdate(changedProperties);
    if (changedProperties.has('transcriptKey')) this.resetTranscriptContext();
    if (changedProperties.has('hasEarlierMessages') && !this.hasEarlierMessages) {
      this.loadEarlierLatched = false;
      this.loadingEarlierObserved = false;
      this.clearEarlierScrollIntent();
    }
    if (changedProperties.has('loadingEarlier')) {
      const wasLoadingEarlier = changedProperties.get('loadingEarlier') === true;
      if (this.loadingEarlier) this.loadingEarlierObserved = true;
      else if (wasLoadingEarlier && this.loadingEarlierObserved) {
        this.loadEarlierLatched = false;
        this.loadingEarlierObserved = false;
      }
    }
    if (changedProperties.has('viewState') && this.viewState) {
      this.clearTranscriptViewState(this.localViewState);
    }
    const registryChanged = this.timelineRegistryRevision !== this.toolRegistry.revision;
    if (
      this.timelineRegistryRevision < 0
      || changedProperties.has('messages')
      || changedProperties.has('permissions')
      || changedProperties.has('questions')
      || changedProperties.has('toolRegistry')
      || changedProperties.has('viewState')
      || changedProperties.has('transcriptKey')
      || registryChanged
    ) {
      this.rebuildTimeline();
    }
  }

  private rebuildTimeline(): void {
    const container = this.scrollContainer;
    const preserveAnchor = this.userUnpinned || !this.autoFollow;
    const anchor = container && preserveAnchor
      ? (this.activeFullAnchor ?? this.captureCurrentAnchor(container))
      : undefined;
    this.cancelFullAnchorRestore();
    const nextItems = this.timelineItems;
    const retainedKeys = new Set(nextItems.map((item) => item.key));
    for (const key of this.measuredVirtualHeights.keys()) {
      if (!retainedKeys.has(key)) this.measuredVirtualHeights.delete(key);
    }
    if (this.explicitRetainedVirtualKey && !retainedKeys.has(this.explicitRetainedVirtualKey)) {
      this.explicitRetainedVirtualKey = undefined;
    }
    for (const key of this.retainedVirtualKeys) {
      if (!retainedKeys.has(key)) this.retainedVirtualKeys.delete(key);
    }
    const messageIds = new Set(this.messages.map((message) => message.id));
    const permissionIds = new Set(this.permissions.map((request) => request.id));
    const questionIds = new Set(this.questions.map((request) => request.id));
    const effectiveViewState = this.effectiveViewState;
    for (const key of Object.keys(effectiveViewState.messages)) {
      if (!messageIds.has(key)) delete effectiveViewState.messages[key];
    }
    for (const key of Object.keys(effectiveViewState.permissions)) {
      if (!permissionIds.has(key)) delete effectiveViewState.permissions[key];
    }
    for (const key of Object.keys(effectiveViewState.questions)) {
      if (!questionIds.has(key)) delete effectiveViewState.questions[key];
    }
    for (const message of this.messages) effectiveViewState.messages[message.id] ??= {};
    for (const request of this.permissions) effectiveViewState.permissions[request.id] ??= {};
    for (const request of this.questions) effectiveViewState.questions[request.id] ??= {};
    for (const message of this.messages) this.syncSubtaskViewState(message);
    this.trackEnteringRows(nextItems);
    this.timelineRenderItems = nextItems;
    this.timelineIndexByKey = new Map(nextItems.map((item, index) => [item.key, index]));
    this.timelineRegistryRevision = this.toolRegistry.revision;
    const shouldVirtualize = nextItems.length > virtualizationThreshold;
    if (!this.virtualizationEnabled && shouldVirtualize) {
      this.virtualizationDisablePending = false;
      if (this.activeTimelineKey()) {
        this.virtualizationEnablePending = true;
        this.connectSelectionChangeListener();
      } else {
        this.captureFullTimelineMeasurements();
        this.virtualizationEnabled = true;
        this.virtualizationEnablePending = false;
        this.disconnectSelectionChangeListener();
      }
    } else if (this.virtualizationEnabled && !shouldVirtualize) {
      this.virtualizationEnablePending = false;
      if (this.activeTimelineKey()) {
        this.virtualizationDisablePending = true;
        this.connectSelectionChangeListener();
      } else {
        this.virtualizationEnabled = false;
        this.virtualizationDisablePending = false;
        this.retainedVirtualKeys.clear();
        this.disconnectSelectionChangeListener();
      }
    } else {
      this.virtualizationEnablePending = false;
      this.virtualizationDisablePending = false;
      if (this.activeSelectionKeys().length === 0) this.disconnectSelectionChangeListener();
    }
    this.recomputeVirtualOffsets();
    if (anchor && container) {
      if (this.virtualizationActive) {
        this.pendingVirtualAnchor = anchor;
        this.measurementAnchor = anchor;
        this.setVirtualRangeAround(anchor.key);
      } else {
        this.pendingVirtualAnchor = undefined;
        this.pendingFullAnchor = anchor;
        this.setInitialVirtualRange();
      }
    } else {
      this.measurementAnchor = undefined;
      this.setInitialVirtualRange();
    }
  }

  /**
   * A tool row that arrives at the tail of a transcript this list has already rendered is the one
   * thing that enters. The first build, a new transcript, earlier history, a row that only
   * re-renders and a row that comes back from eviction are all simply there, and a reader who
   * asked for less motion gets none: the marker is never set rather than set and then ignored.
   */
  private trackEnteringRows(nextItemsArg: TTimelineItem[]): void {
    const nextKeys = new Set(nextItemsArg.map((item) => item.key));
    for (const key of this.enteringKeys) if (!nextKeys.has(key)) this.enteringKeys.delete(key);
    if (this.timelineBuilt && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      for (let index = nextItemsArg.length - 1; index >= 0; index -= 1) {
        const item = nextItemsArg[index]!;
        if (this.knownTimelineKeys.has(item.key)) break;
        if (item.kind === 'message' && item.toolRow) this.enteringKeys.add(item.key);
      }
    }
    this.knownTimelineKeys.clear();
    for (const key of nextKeys) this.knownTimelineKeys.add(key);
    this.timelineBuilt = true;
  }

  /**
   * An entrance belongs to the row that is mounted for it. Three things end one, and between them
   * a key can never outlive its row: the animation finishing or being cancelled (below), a whole
   * transcript going away (`resetTranscriptContext` on a new `transcriptKey`, and
   * `disconnectedCallback`), and this — a row that left the DOM without either, which is what an
   * eviction from the virtual window or a switch between the two stacks does. So a row that mounts
   * again is an ordinary mount and cannot replay what it never finished.
   */
  private pruneEnteringRows(): void {
    if (!this.enteringKeys.size) return;
    const mounted = new Set(
      [...this.shadowRoot?.querySelectorAll<HTMLElement>('[data-entering]') ?? []]
        .map((row) => row.dataset.virtualKey ?? row.dataset.timelineKey),
    );
    for (const key of this.enteringKeys) if (!mounted.has(key)) this.enteringKeys.delete(key);
  }

  /**
   * An arrival happens once: a later remount of the same row is an ordinary mount. The listener
   * sits on the row that carries the animation, because that is the only element guaranteed to see
   * both the end and a cancellation.
   */
  private readonly handleRowEntranceEnd = (eventArg: AnimationEvent): void => {
    if (eventArg.animationName !== 'harness-row-blend') return;
    const target = eventArg.currentTarget;
    if (!(target instanceof HTMLElement)) return;
    const key = target.dataset.virtualKey ?? target.dataset.timelineKey;
    if (key && this.enteringKeys.delete(key)) this.requestUpdate();
  };

  private pruneViewState(
    stateArg: IHarnessTranscriptViewState,
    messageIdsArg: string[],
    permissionIdsArg: string[],
    questionIdsArg: string[],
  ): void {
    const messageIds = new Set(messageIdsArg);
    const permissionIds = new Set(permissionIdsArg);
    const questionIds = new Set(questionIdsArg);
    for (const key of Object.keys(stateArg.messages)) if (!messageIds.has(key)) delete stateArg.messages[key];
    for (const key of Object.keys(stateArg.permissions)) if (!permissionIds.has(key)) delete stateArg.permissions[key];
    for (const key of Object.keys(stateArg.questions)) if (!questionIds.has(key)) delete stateArg.questions[key];
  }

  private clearTranscriptViewState(stateArg: IHarnessTranscriptViewState): void {
    for (const key of Object.keys(stateArg.messages)) delete stateArg.messages[key];
    for (const key of Object.keys(stateArg.permissions)) delete stateArg.permissions[key];
    for (const key of Object.keys(stateArg.questions)) delete stateArg.questions[key];
  }

  private syncSubtaskViewState(messageArg: IHarnessMessage): void {
    const messageState = this.effectiveViewState.messages[messageArg.id];
    if (!messageState) return;
    const subtask = messageArg.toolCall?.subtask;
    // The thread's view state is kept for as long as there is a thread to reopen in the modal.
    if (!subtask || subtask.previewState !== 'ready') {
      delete messageState.subtaskTranscript;
      delete messageState.subtaskTranscriptSessionId;
      return;
    }
    if (
      messageState.subtaskTranscriptSessionId !== subtask.sessionId
      || !messageState.subtaskTranscript
    ) {
      messageState.subtaskTranscriptSessionId = subtask.sessionId;
      messageState.subtaskTranscript = { messages: {}, permissions: {}, questions: {} };
    }
    this.pruneViewState(
      messageState.subtaskTranscript,
      subtask.messages.map((entry) => entry.id),
      (subtask.permissions ?? []).map((entry) => entry.id),
      (subtask.questions ?? []).map((entry) => entry.id),
    );
  }

  private get virtualizationActive(): boolean {
    return this.virtualizationEnabled;
  }

  private estimatedVirtualHeight(itemArg: TTimelineItem): number {
    if (itemArg.kind === 'permission') return 150;
    if (itemArg.kind === 'question') return 180;
    if (itemArg.toolRow) return toolRowHeightPx;
    if (itemArg.message.reasoning?.some((part) => !part.endedAt)) return 220;
    return itemArg.message.text.length > 1_000 ? 180 : 96;
  }

  /**
   * A run of collapsed tool rows reads as one list, so they sit at the row gap instead of the
   * transcript gap. An opened row is not part of that run and keeps the normal distance; the
   * flowed stack says the same thing with a margin on the row itself.
   */
  private gapAfter(itemArg: TTimelineItem, nextArg: TTimelineItem): number {
    const tightRow = (candidateArg: TTimelineItem): boolean =>
      candidateArg.kind === 'message' && candidateArg.toolRow && !this.messageToolOpen(candidateArg);
    return tightRow(itemArg) && tightRow(nextArg) ? toolRowGapPx : virtualGapPx;
  }

  private recomputeVirtualOffsets(): void {
    const offsets: number[] = [];
    let offset = 0;
    for (const [index, item] of this.timelineRenderItems.entries()) {
      offsets.push(offset);
      offset += this.measuredVirtualHeights.get(item.key) ?? this.estimatedVirtualHeight(item);
      const next = this.timelineRenderItems[index + 1];
      if (next) offset += this.gapAfter(item, next);
    }
    this.virtualOffsets = offsets;
    this.virtualTotalHeight = offset;
  }

  private setInitialVirtualRange(): void {
    if (!this.virtualizationActive) {
      this.virtualStartIndex = 0;
      this.virtualEndIndex = this.timelineRenderItems.length;
      return;
    }
    const initialCount = Math.min(this.timelineRenderItems.length, 24);
    this.virtualEndIndex = this.autoFollow ? this.timelineRenderItems.length : initialCount;
    this.virtualStartIndex = this.autoFollow ? this.virtualEndIndex - initialCount : 0;
  }

  private setVirtualRangeAround(keyArg: string): void {
    const anchorIndex = this.timelineIndexByKey.get(keyArg);
    if (anchorIndex === undefined) {
      this.setInitialVirtualRange();
      return;
    }
    this.virtualStartIndex = Math.max(0, anchorIndex - 12);
    this.virtualEndIndex = Math.min(this.timelineRenderItems.length, anchorIndex + 13);
  }

  private captureFullTimelineMeasurements(): void {
    for (const element of this.shadowRoot?.querySelectorAll<HTMLElement>('.stack > [data-timeline-key]') ?? []) {
      const key = element.dataset.timelineKey;
      const height = element?.getBoundingClientRect().height ?? 0;
      if (key && height > 0) this.measuredVirtualHeights.set(key, height);
    }
  }

  private captureCurrentAnchor(containerArg: HTMLElement): { key: string; offset: number } | undefined {
    if (this.virtualizationActive) return this.captureVirtualAnchor(containerArg);
    const containerTop = containerArg.getBoundingClientRect().top;
    const itemElements = Array.from(
      this.shadowRoot?.querySelectorAll<HTMLElement>('.stack > [data-timeline-key]') ?? [],
    );
    const element = itemElements.find((candidate) => candidate.getBoundingClientRect().bottom > containerTop);
    const key = element?.dataset.timelineKey;
    return key ? { key, offset: containerTop - element.getBoundingClientRect().top } : undefined;
  }

  public render(): TemplateResult {
    const items = this.timelineRenderItems;
    return html`
      <div
        class=${`scroll ${this.hideScrollbar ? 'hideScrollbar' : ''} ${this.scrollFadeTop ? 'fadeTop' : ''} ${this.scrollFadeBottom ? 'fadeBottom' : ''}`}
        @scroll=${this.handleScroll}
        @scrollend=${this.handleScrollEnd}
        @wheel=${this.handleUserScrollIntent}
        @touchstart=${this.handleTouchStart}
        @touchmove=${this.handleUserScrollIntent}
        @touchend=${this.handleTouchEnd}
        @touchcancel=${this.handleTouchCancel}
        @pointerdown=${this.handlePointerDown}
        @pointerup=${this.handlePointerUp}
        @pointercancel=${this.handlePointerCancel}
        @keydown=${this.handleUserScrollIntent}
        @keyup=${this.handleProtectedStateChange}
        @focusout=${this.handleProtectedStateChange}
        @harness-card-retention=${this.handleCardRetention}
        @harness-tool-disclosure=${this.handleToolDisclosure}
      >
        <div class="scrollContent">
        <slot name="leading"></slot>
        ${items.length
          ? this.virtualizationActive ? this.renderVirtualTimeline() : this.renderFullTimeline(items)
          : html`
              <div class="empty">
                <slot name="empty">
                  <div class="emptyTitle">${this.emptyTitle}</div>
                  <div class="emptyText">${this.emptyText}</div>
                </slot>
              </div>
            `}
        <div class="statusRow">
          <dees-harness-status .status=${this.status}></dees-harness-status>
        </div>
        </div>
      </div>
      ${this.showJumpChip
        ? html`
            <button class="jumpChip" type="button" @click=${() => this.scrollToBottom(true)}>
              <dees-icon icon="lucide:ArrowDown" iconSize="12"></dees-icon>
              Jump to latest
            </button>
          `
        : ''}
    `;
  }

  private renderFullTimeline(itemsArg: TTimelineItem[]): TemplateResult {
    return html`
      <div class="stack" role="log" aria-label="Conversation transcript">
        ${repeat(
          itemsArg,
          (item) => item.key,
          (item, index) => this.renderTimelineItem(item, index),
        )}
      </div>
    `;
  }

  private renderVirtualTimeline(): TemplateResult {
    const indexedItems = this.virtualRenderedIndexes().map((index) => ({
      index,
      item: this.timelineRenderItems[index]!,
    }));
    return html`
      <div
        class="virtualStack"
        role="log"
        aria-label="Conversation transcript"
        style=${`height: ${this.virtualTotalHeight}px;`}
      >
        ${repeat(
          indexedItems,
          (entry) => entry.item.key,
          (entry) => html`
            <article
              class="virtualItem"
              data-virtual-key=${entry.item.key}
              ?data-tool-row=${entry.item.kind === 'message' && entry.item.toolRow}
              ?data-tool-open=${entry.item.kind === 'message' && this.messageToolOpen(entry.item)}
              ?data-entering=${this.enteringKeys.has(entry.item.key)}
              @animationend=${this.handleRowEntranceEnd}
              @animationcancel=${this.handleRowEntranceEnd}
              aria-posinset=${entry.index + 1}
              aria-setsize=${this.timelineRenderItems.length}
              style=${`--harness-virtual-offset: ${this.virtualOffsets[entry.index] ?? 0}px;`}
            >
              ${this.renderTimelineItem(entry.item, entry.index)}
            </article>
          `,
        )}
      </div>
    `;
  }

  private virtualRenderedIndexes(): number[] {
    const indexes: number[] = [];
    for (let index = this.virtualStartIndex; index < this.virtualEndIndex; index += 1) {
      indexes.push(index);
    }
    for (const retainedKey of this.retainedVirtualKeys) {
      const retainedIndex = this.timelineIndexByKey.get(retainedKey);
      if (retainedIndex !== undefined && !indexes.includes(retainedIndex)) indexes.push(retainedIndex);
    }
    return indexes.sort((left, right) => left - right);
  }

  /**
   * Whether a tool row has its body open, taken from the retained view state at render time
   * rather than from the rendered card: the row carries the measure, so it has to know before its
   * first style resolution or a remounted open row would animate into its width.
   */
  private messageToolOpen(itemArg: TMessageTimelineItem): boolean {
    return itemArg.toolRow
      && this.effectiveViewState.messages[itemArg.message.id]?.toolExpanded === true;
  }

  private renderTimelineItem(itemArg: TTimelineItem, logicalIndexArg: number): TemplateResult {
    if (itemArg.kind === 'message') return this.renderMessage(itemArg);
    if (itemArg.kind === 'permission') {
      return html`
        <dees-harness-permission-card
          data-timeline-key=${itemArg.key}
          aria-posinset=${logicalIndexArg + 1}
          aria-setsize=${this.timelineRenderItems.length}
          .request=${itemArg.request}
          .busy=${this.permissionsBusy}
          .viewState=${this.permissionViewState(itemArg.request.id)}
        ></dees-harness-permission-card>
      `;
    }
    return html`
      <dees-harness-question-card
        data-timeline-key=${itemArg.key}
        aria-posinset=${logicalIndexArg + 1}
        aria-setsize=${this.timelineRenderItems.length}
        .request=${itemArg.question}
        .busy=${this.permissionsBusy}
        .viewState=${this.questionViewState(itemArg.question.id)}
      ></dees-harness-question-card>
    `;
  }

  private renderMessage(itemArg: TMessageTimelineItem): TemplateResult {
    return html`
      <dees-harness-message
        data-timeline-key=${itemArg.key}
        data-message-id=${itemArg.message.id}
        ?data-tool-row=${itemArg.toolRow}
        ?data-tool-open=${this.messageToolOpen(itemArg)}
        ?data-entering=${!this.virtualizationActive && this.enteringKeys.has(itemArg.key)}
        @animationend=${this.handleRowEntranceEnd}
        @animationcancel=${this.handleRowEntranceEnd}
        .message=${itemArg.message}
        .showRole=${this.showRoles}
        .showTimestamp=${this.showTimestamps}
        .toolRegistry=${this.toolRegistry}
        .markdownWhileStreaming=${this.markdownWhileStreaming}
        .viewState=${this.messageViewState(itemArg.message.id)}
      ></dees-harness-message>
    `;
  }

  private messageViewState(messageIdArg: string): IHarnessMessageViewState {
    return this.effectiveViewState.messages[messageIdArg]!;
  }

  private permissionViewState(requestIdArg: string): IHarnessPermissionViewState {
    return this.effectiveViewState.permissions[requestIdArg]!;
  }

  private questionViewState(requestIdArg: string): IHarnessQuestionViewState {
    return this.effectiveViewState.questions[requestIdArg]!;
  }

  private get effectiveViewState(): IHarnessTranscriptViewState {
    return this.viewState ?? this.localViewState;
  }

  // ------------------------------------------------------------------ deltas

  /**
   * Routes an incremental update to the affected message. Mutates the
   * message object held in `.messages` so a later full re-assignment stays
   * idempotent; only the target element re-renders.
   */
  public applyDelta(delta: THarnessStreamDelta): void {
    if (delta.parentMessageId) {
      const parentMessage = this.messages.find((candidate) => candidate.id === delta.parentMessageId);
      if (!parentMessage) return;
      const parentElement = this.messageElement(delta.parentMessageId);
      const { parentMessageId: _parentMessageId, ...childDelta } = delta;
      const localDelta = childDelta as THarnessMessageStreamDelta;
      const appliedByElement = parentElement?.applySubtaskDelta(localDelta) ?? false;
      if (!appliedByElement && parentMessage.toolCall?.subtask?.previewState === 'ready') {
        if (harnessApplyMessageDelta(parentMessage.toolCall.subtask.messages, localDelta)) {
          parentElement?.requestUpdate();
        }
      }
      this.followAfterContentChange();
      return;
    }
    const message = this.messages.find((candidate) => candidate.id === delta.messageId);
    if (!message) return;
    const element = this.messageElement(delta.messageId);
    switch (delta.type) {
      case 'text':
        if (element) element.appendTextDelta(delta.delta);
        else harnessApplyMessageDelta(this.messages, delta);
        break;
      case 'reasoning-start':
        if (element) element.appendReasoningDelta(delta.partId, '', delta.startedAt);
        else harnessApplyMessageDelta(this.messages, delta);
        break;
      case 'reasoning':
        if (element) element.appendReasoningDelta(delta.partId, delta.delta);
        else harnessApplyMessageDelta(this.messages, delta);
        break;
      case 'reasoning-end':
        if (element) element.endReasoning(delta.partId, delta.endedAt);
        else harnessApplyMessageDelta(this.messages, delta);
        break;
      case 'tool-update': {
        const wasToolRow = harnessRendersToolRow(message);
        message.toolCall = { ...(message.toolCall ?? { id: delta.tool.id, name: '', status: 'pending' }), ...delta.tool };
        this.syncSubtaskViewState(message);
        element?.requestUpdate();
        // The first tool update turns a plain message into a row, which the transcript spaces
        // and measures differently.
        if (wasToolRow !== harnessRendersToolRow(message)) {
          this.rebuildTimeline();
          this.requestUpdate();
        }
        break;
      }
      case 'message-end':
        if (element) element.endStream(delta.usage, delta.error);
        else harnessApplyMessageDelta(this.messages, delta);
        break;
    }
    this.followAfterContentChange();
  }

  /**
   * Reconciles host mutations made directly to the shared message objects.
   * Omit IDs for structural changes; an empty ID list is intentionally a no-op.
   */
  public refreshMessages(messageIdsArg?: readonly string[]): void {
    if (messageIdsArg?.length === 0) return;
    const messageIds = messageIdsArg ? new Set(messageIdsArg) : undefined;
    if (!messageIds) {
      this.rebuildTimeline();
      this.requestUpdate();
    }
    for (const element of this.shadowRoot?.querySelectorAll(
      'dees-harness-message[data-message-id]',
    ) ?? []) {
      if (!(element instanceof DeesHarnessMessage)) continue;
      if (messageIds && !messageIds.has(element.message.id)) continue;
      element.refreshMessage();
    }
    this.followAfterContentChange();
  }

  private messageElement(messageId: string): DeesHarnessMessage | undefined {
    const element = this.shadowRoot?.querySelector(`dees-harness-message[data-message-id="${CSS.escape(messageId)}"]`);
    return element instanceof DeesHarnessMessage ? element : undefined;
  }

  // ------------------------------------------------------------------ scroll

  private get scrollContainer(): HTMLElement | null {
    return this.shadowRoot?.querySelector('.scroll') ?? null;
  }

  public get isPinnedToBottom(): boolean {
    const container = this.scrollContainer;
    if (!container) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight < pinThresholdPx;
  }

  private contentResizeObserver: ResizeObserver | null = null;
  private observedScrollContent: Element | null = null;
  private observedContentBlockSize: number | null = null;
  private followFrame: number | null = null;
  private followSecondFrame: number | null = null;
  // Intent-based following: the list stays pinned until the user explicitly
  // moves away from the bottom (wheel/touch/keyboard or a scrollbar drag), and
  // re-pins the moment they return. Late layout growth — async markdown parses,
  // images — re-pins through the content ResizeObserver for as long as the user
  // has not left the bottom, so following never expires mid-stream and never
  // fights the user.
  private userUnpinned = false;
  private lastObservedScrollTop = 0;

  private repinToBottom = (): void => {
    if (!this.autoFollow || this.userUnpinned) return;
    const container = this.scrollContainer;
    if (!container) return;
    this.setProgrammaticScrollTop(container, container.scrollHeight);
  };

  private followAfterContentChange(): void {
    if (!this.autoFollow) return;
    if (this.userUnpinned) {
      this.showJumpChip = true;
      return;
    }
    this.repinAfterContentGrowth();
    // Catch post-render growth; later asynchronous growth uses the observer.
    if (this.followFrame !== null || this.followSecondFrame !== null) return;
    this.followFrame = requestAnimationFrame(() => {
      this.followFrame = null;
      if (!this.isConnected) return;
      this.repinAfterContentGrowth();
      this.followSecondFrame = requestAnimationFrame(() => {
        this.followSecondFrame = null;
        if (!this.isConnected) return;
        this.repinAfterContentGrowth();
      });
    });
  }

  private handleUserScrollIntent = (event: Event): void => {
    if (this.eventComesFromNestedMessageList(event)) return;
    if (event.type === 'wheel') {
      this.cancelFullAnchorRestore();
      const deltaY = (event as WheelEvent).deltaY;
      if (deltaY < 0) {
        this.armEarlierScrollIntent();
        this.requestEarlierIfAtTop();
        this.markUserUnpinned();
      } else if (deltaY > 0) {
        this.clearEarlierScrollIntent();
      }
      return;
    }
    if (event.type === 'keydown') {
      if (this.keyboardEventOriginIsInteractive(event as KeyboardEvent)) return;
      const key = (event as KeyboardEvent).key;
      if (['ArrowUp', 'PageUp', 'Home'].includes(key)) {
        this.cancelFullAnchorRestore();
        this.armEarlierScrollIntent();
        this.requestEarlierIfAtTop();
        this.markUserUnpinned();
      } else if (['ArrowDown', 'PageDown', 'End', ' '].includes(key)) {
        this.clearEarlierScrollIntent();
        this.cancelFullAnchorRestore();
      }
      return;
    }
    if (event.type === 'touchmove') {
      const currentY = (event as TouchEvent).touches[0]?.clientY;
      const previousY = this.touchScrollY;
      this.touchScrollY = currentY;
      if (currentY === undefined || previousY === undefined) return;
      if (currentY > previousY + 0.5) {
        this.armEarlierScrollIntent();
        this.requestEarlierIfAtTop();
      } else if (currentY < previousY - 0.5) {
        this.clearEarlierScrollIntent();
      } else {
        return;
      }
      this.cancelFullAnchorRestore();
      this.markUserUnpinned();
      return;
    }
    if (event.type === 'pointerdown') {
      if ((event as PointerEvent).button !== 0) return;
      if (event.target !== this.scrollContainer) return;
      this.scrollbarGestureActive = true;
      this.cancelFullAnchorRestore();
      this.markUserUnpinned();
    }
  };

  private markUserUnpinned(): void {
    this.userUnpinned = true;
    this.measurementAnchor = undefined;
  }

  private eventComesFromNestedMessageList(eventArg: Event): boolean {
    return eventArg.composedPath().some(
      (candidate) => candidate instanceof DeesHarnessMessageList && candidate !== this,
    );
  }

  private keyboardEventOriginIsInteractive(eventArg: KeyboardEvent): boolean {
    return eventArg.composedPath().some((candidate) => (
      candidate instanceof HTMLElement
      && candidate !== this
      && (
        candidate.isContentEditable
        || candidate.matches('input, textarea, select, button, [contenteditable]')
      )
    ));
  }

  private armEarlierScrollIntent(): void {
    this.earlierScrollIntent = true;
    this.scheduleScrollIntentSettlement();
  }

  private scheduleScrollIntentSettlement(): void {
    if (this.scrollIntentTimer) clearTimeout(this.scrollIntentTimer);
    this.scrollIntentTimer = setTimeout(() => {
      this.scrollIntentTimer = undefined;
      this.earlierScrollIntent = false;
    }, scrollIntentSettlementMs);
  }

  private clearEarlierScrollIntent(): void {
    if (this.scrollIntentTimer) clearTimeout(this.scrollIntentTimer);
    this.scrollIntentTimer = undefined;
    this.earlierScrollIntent = false;
  }

  private requestEarlierIfAtTop(): void {
    const container = this.scrollContainer;
    if (
      !container
      || container.scrollTop > loadEarlierThresholdPx
      || !this.hasEarlierMessages
      || this.loadingEarlier
      || this.loadEarlierLatched
    ) return;
    this.loadEarlierLatched = true;
    this.clearEarlierScrollIntent();
    this.dispatchEvent(new CustomEvent('harness-load-earlier', {
      bubbles: true,
      composed: true,
    }));
  }

  private resetLoadEarlierState(): void {
    this.loadEarlierLatched = false;
    this.loadingEarlierObserved = false;
    this.scrollbarGestureActive = false;
    this.touchScrollY = undefined;
    this.clearEarlierScrollIntent();
  }

  private setProgrammaticScrollTop(containerArg: HTMLElement, topArg: number): void {
    this.clearProgrammaticScrollMarker();
    const generation = ++this.programmaticScrollGeneration;
    containerArg.scrollTop = topArg;
    this.programmaticScrollTop = containerArg.scrollTop;
    this.lastObservedScrollTop = containerArg.scrollTop;
    this.programmaticScrollFrame = requestAnimationFrame(() => {
      this.programmaticScrollFrame = null;
      if (generation !== this.programmaticScrollGeneration) return;
      this.programmaticScrollSecondFrame = requestAnimationFrame(() => {
        this.programmaticScrollSecondFrame = null;
        if (generation === this.programmaticScrollGeneration) {
          this.programmaticScrollTop = undefined;
        }
      });
    });
  }

  private consumeProgrammaticScroll(topArg: number): boolean {
    if (this.programmaticScrollTop === undefined) return false;
    const matches = Math.abs(this.programmaticScrollTop - topArg) <= 1;
    this.clearProgrammaticScrollMarker();
    return matches;
  }

  private clearProgrammaticScrollMarker(): void {
    this.programmaticScrollGeneration += 1;
    if (this.programmaticScrollFrame !== null) cancelAnimationFrame(this.programmaticScrollFrame);
    if (this.programmaticScrollSecondFrame !== null) cancelAnimationFrame(this.programmaticScrollSecondFrame);
    this.programmaticScrollFrame = null;
    this.programmaticScrollSecondFrame = null;
    this.programmaticScrollTop = undefined;
  }

  private resetTranscriptContext(): void {
    this.cancelFullAnchorRestore();
    if (this.followFrame !== null) cancelAnimationFrame(this.followFrame);
    if (this.followSecondFrame !== null) cancelAnimationFrame(this.followSecondFrame);
    if (this.virtualMeasureFrame !== null) cancelAnimationFrame(this.virtualMeasureFrame);
    if (this.virtualRangeFrame !== null) cancelAnimationFrame(this.virtualRangeFrame);
    if (this.virtualizationActivationFrame !== null) cancelAnimationFrame(this.virtualizationActivationFrame);
    this.followFrame = null;
    this.followSecondFrame = null;
    this.virtualMeasureFrame = null;
    this.virtualRangeFrame = null;
    this.virtualizationActivationFrame = null;
    this.pendingVirtualAnchor = undefined;
    this.pendingFullAnchor = undefined;
    this.measurementAnchor = undefined;
    this.pendingVirtualRepin = false;
    this.virtualizationEnabled = false;
    this.virtualizationEnablePending = false;
    this.virtualizationDisablePending = false;
    this.virtualStartIndex = 0;
    this.virtualEndIndex = 0;
    this.retainedVirtualKeys.clear();
    this.explicitRetainedVirtualKey = undefined;
    this.measuredVirtualHeights.clear();
    this.virtualOffsets = [];
    this.virtualTotalHeight = 0;
    this.timelineRegistryRevision = -1;
    this.knownTimelineKeys.clear();
    this.enteringKeys.clear();
    this.timelineBuilt = false;
    this.clearTranscriptViewState(this.localViewState);
    this.clearSelectionGesture();
    this.disconnectSelectionChangeListener();
    this.userUnpinned = false;
    this.lastObservedScrollTop = 0;
    this.showJumpChip = false;
    this.resetLoadEarlierState();
    this.clearProgrammaticScrollMarker();
  }

  private readonly handleTouchStart = (eventArg: TouchEvent): void => {
    if (this.eventComesFromNestedMessageList(eventArg)) return;
    this.touchScrollY = eventArg.touches[0]?.clientY;
  };

  private readonly handleTouchEnd = (): void => {
    this.touchScrollY = undefined;
    if (this.earlierScrollIntent) this.scheduleScrollIntentSettlement();
  };

  private readonly handleTouchCancel = (): void => {
    this.touchScrollY = undefined;
    this.clearEarlierScrollIntent();
  };

  public scrollToBottom(force = false): void {
    const container = this.scrollContainer;
    if (!container) return;
    if (force) {
      this.cancelFullAnchorRestore();
      this.userUnpinned = false;
      this.measurementAnchor = undefined;
    }
    if (force || this.isPinnedToBottom) {
      this.setProgrammaticScrollTop(container, container.scrollHeight);
      this.showJumpChip = false;
    }
    this.syncScrollEdgeFades();
  }

  /** Scrolls to and focuses the first unanswered permission request. */
  public async focusPendingPermission(): Promise<boolean> {
    const request = this.permissions.find((candidate) => !candidate.response);
    if (!request) return false;
    this.scrollToBottom(true);
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await this.updateComplete;
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    const key = `permission:${request.id}`;
    const card = Array.from(
      this.shadowRoot?.querySelectorAll<DeesHarnessPermissionCard>(
        'dees-harness-permission-card[data-timeline-key]',
      ) ?? [],
    ).find((candidate) => candidate.dataset.timelineKey === key);
    if (!card) return false;
    card.scrollIntoView({ block: 'center' });
    return card.focusFirstAction();
  }

  private handleScroll = (): void => {
    const container = this.scrollContainer;
    if (!container) return;
    const top = container.scrollTop;
    const previousTop = this.lastObservedScrollTop;
    const programmatic = this.consumeProgrammaticScroll(top);
    if (programmatic) {
      this.clearEarlierScrollIntent();
    } else {
      if (this.scrollbarGestureActive && top < previousTop - 0.5) {
        this.armEarlierScrollIntent();
      } else if (top > previousTop + 0.5) {
        this.clearEarlierScrollIntent();
      } else if (this.earlierScrollIntent) {
        this.scheduleScrollIntentSettlement();
      }
      if (this.earlierScrollIntent) this.requestEarlierIfAtTop();
    }
    this.syncScrollEdgeFades();
    if (this.isPinnedToBottom) {
      // reaching the bottom — by any means — resumes following
      this.userUnpinned = false;
      if (this.showJumpChip) {
        this.showJumpChip = false;
      }
    }
    this.lastObservedScrollTop = top;
    this.scheduleVirtualRangeUpdate();
    this.schedulePendingVirtualizationActivation();
  };

  private readonly handleScrollEnd = (): void => {
    this.clearEarlierScrollIntent();
  };

  private schedulePendingVirtualizationActivation(): void {
    if (
      (!this.virtualizationEnablePending && !this.virtualizationDisablePending)
      || this.virtualizationActivationFrame !== null
    ) return;
    this.virtualizationActivationFrame = requestAnimationFrame(() => {
      this.virtualizationActivationFrame = null;
      if (!this.isConnected || this.activeTimelineKey()) return;
      const container = this.scrollContainer;
      const anchor = container ? this.captureCurrentAnchor(container) : undefined;
      if (this.virtualizationEnablePending) {
        this.captureFullTimelineMeasurements();
        this.virtualizationEnabled = true;
        this.virtualizationEnablePending = false;
        this.recomputeVirtualOffsets();
        if (anchor) {
          this.pendingVirtualAnchor = anchor;
          this.setVirtualRangeAround(anchor.key);
        } else {
          this.setInitialVirtualRange();
        }
      } else if (this.virtualizationDisablePending) {
        this.virtualizationEnabled = false;
        this.virtualizationDisablePending = false;
        this.retainedVirtualKeys.clear();
        if (anchor) this.pendingFullAnchor = anchor;
        this.setInitialVirtualRange();
      }
      this.disconnectSelectionChangeListener();
      this.requestUpdate();
    });
  }

  private readonly handleSelectionChange = (): void => {
    const selection = document.getSelection();
    if (
      !selection
      || selection.rangeCount === 0
      || (
        !this.selectionGestureActive
        && this.selectionGestureSnapshot
        && !this.selectionMatchesGestureSnapshot(selection)
      )
      || (
        !this.selectionGestureActive
        && selection.isCollapsed
        && !this.hasCrossShadowSelectionFallback
      )
    ) this.clearSelectionGesture();
    this.scheduleVirtualRangeUpdate();
    this.schedulePendingVirtualizationActivation();
  };

  private readonly handlePointerDown = (eventArg: PointerEvent): void => {
    this.handleUserScrollIntent(eventArg);
    if (eventArg.button !== 0) return;
    this.clearSelectionGesture();
    this.selectionGestureActive = true;
    this.selectionGestureStartOwner = this.selectionOwnerForEvent(eventArg);
    const key = this.timelineKeyForEvent(eventArg);
    if (key) this.selectionGestureKeys.add(key);
  };

  private readonly handlePointerUp = (eventArg: PointerEvent): void => {
    this.scrollbarGestureActive = false;
    if (eventArg.button !== 0) return;
    const key = this.timelineKeyForEvent(eventArg);
    if (key) this.selectionGestureKeys.add(key);
    const endOwner = this.selectionOwnerForEvent(eventArg);
    this.selectionGestureCrossesOwners = Boolean(
      this.selectionGestureStartOwner
      && endOwner
      && this.selectionGestureStartOwner !== endOwner,
    );
    this.selectionGestureActive = false;
    this.selectionGestureSnapshot = this.captureSelectionSnapshot(document.getSelection());
    if (!this.hasCrossShadowSelectionFallback && document.getSelection()?.isCollapsed !== false) {
      this.clearSelectionGesture();
    }
    this.handleProtectedStateChange();
  };

  private readonly handlePointerCancel = (): void => {
    this.scrollbarGestureActive = false;
    this.clearEarlierScrollIntent();
    this.clearSelectionGesture();
    this.handleProtectedStateChange();
  };

  private readonly handleProtectedStateChange = (): void => {
    this.scheduleVirtualRangeUpdate();
    this.schedulePendingVirtualizationActivation();
  };

  private connectSelectionChangeListener(): void {
    if (this.selectionChangeListening) return;
    document.addEventListener('selectionchange', this.handleSelectionChange);
    this.selectionChangeListening = true;
  }

  private disconnectSelectionChangeListener(): void {
    if (!this.selectionChangeListening) return;
    document.removeEventListener('selectionchange', this.handleSelectionChange);
    this.selectionChangeListening = false;
  }

  public updated(changedProperties: Map<string, any>): void {
    super.updated(changedProperties);
    this.pruneEnteringRows();
    if (changedProperties.has('transcriptKey')) {
      const container = this.scrollContainer;
      if (container) {
        if (this.autoFollow) this.scrollToBottom(true);
        else this.setProgrammaticScrollTop(container, 0);
      }
    }
    if (changedProperties.has('messages') || changedProperties.has('status') || changedProperties.has('permissions') || changedProperties.has('questions')) {
      this.followAfterContentChange();
    }
    if (changedProperties.has('autoFollow')) {
      if (this.autoFollow) {
        this.connectContentResizeObserver();
        this.scrollToBottom(true);
      }
      else this.disconnectContentResizeObserver();
    }
    if (
      changedProperties.has('fadeScrollEdges')
      || changedProperties.has('messages')
      || changedProperties.has('permissions')
      || changedProperties.has('questions')
    ) this.syncScrollEdgeFades();
    if (this.virtualizationActive) {
      this.connectVirtualObservers();
      if (this.pendingVirtualAnchor) {
        this.restoreVirtualAnchor(this.scrollContainer!, this.pendingVirtualAnchor);
        this.pendingVirtualAnchor = undefined;
      } else if (this.pendingVirtualRepin) {
        this.repinToBottom();
        this.pendingVirtualRepin = false;
      }
      this.scheduleVirtualRangeUpdate();
    } else {
      this.disconnectVirtualObservers();
      if (this.pendingFullAnchor) {
        this.scheduleFullAnchorRestore(this.scrollContainer!, this.pendingFullAnchor);
        this.pendingFullAnchor = undefined;
      }
    }
  }

  public firstUpdated(changedProperties: Map<string, any>): void {
    super.firstUpdated(changedProperties);
    if (!this.isConnected) return;
    this.connectContentResizeObserver();
    this.repinToBottom();
    this.syncScrollEdgeFades();
    if (this.virtualizationActive) this.connectVirtualObservers();
  }

  public async connectedCallback(): Promise<void> {
    const connected = super.connectedCallback();
    if (this.virtualizationEnablePending || this.virtualizationDisablePending) {
      this.connectSelectionChangeListener();
    }
    if (this.hasUpdated) {
      this.connectContentResizeObserver();
      if (this.virtualizationActive) this.connectVirtualObservers();
      this.schedulePendingVirtualizationActivation();
    }
    await connected;
  }

  private connectContentResizeObserver(): void {
    if (!this.isConnected || !this.autoFollow) return;
    const content = this.shadowRoot?.querySelector('.scrollContent');
    if (!content || (this.contentResizeObserver && content === this.observedScrollContent)) return;
    this.contentResizeObserver?.disconnect();
    this.observedContentBlockSize = content.getBoundingClientRect().height;
    this.contentResizeObserver = new ResizeObserver((entries) => {
      const entry = entries.find((candidate) => candidate.target === this.observedScrollContent);
      if (!entry) return;
      const blockSize = entry.borderBoxSize[0]?.blockSize ?? entry.contentRect.height;
      this.handleContentBlockSize(blockSize);
    });
    this.contentResizeObserver.observe(content);
    this.observedScrollContent = content;
  }

  private connectVirtualObservers(): void {
    const container = this.scrollContainer;
    if (!container) return;
    this.virtualViewportResizeObserver ??= new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (width > 0 && Math.abs(width - this.lastVirtualViewportWidth) > 0.5) {
        const preserveAnchor = this.userUnpinned || !this.autoFollow;
        const anchor = preserveAnchor ? this.captureVirtualMetricAnchor(container) : undefined;
        this.lastVirtualViewportWidth = width;
        this.measuredVirtualHeights.clear();
        this.recomputeVirtualOffsets();
        if (anchor) this.pendingVirtualAnchor = anchor;
        else this.pendingVirtualRepin = true;
        this.requestUpdate();
        this.scheduleVirtualRangeUpdate(true);
      }
    });
    this.virtualViewportResizeObserver.disconnect();
    this.virtualViewportResizeObserver.observe(container);
    this.lastVirtualViewportWidth ||= container.getBoundingClientRect().width;

    this.virtualRowResizeObserver ??= new ResizeObserver(() => this.scheduleVirtualMeasurements());
    this.virtualRowResizeObserver.disconnect();
    for (const row of this.shadowRoot?.querySelectorAll<HTMLElement>('.virtualItem') ?? []) {
      this.virtualRowResizeObserver.observe(row);
    }
  }

  private scheduleVirtualMeasurements(): void {
    if (this.virtualMeasureFrame !== null) return;
    this.virtualMeasureFrame = requestAnimationFrame(() => {
      this.virtualMeasureFrame = null;
      this.measureVirtualRows();
    });
  }

  private measureVirtualRows(): void {
    const container = this.scrollContainer;
    if (!container || !this.virtualizationActive) return;
    const preserveAnchor = this.userUnpinned || !this.autoFollow;
    const anchor = preserveAnchor
      ? (this.measurementAnchor ?? this.captureVirtualMetricAnchor(container))
      : undefined;
    let changed = false;
    for (const row of this.shadowRoot?.querySelectorAll<HTMLElement>('.virtualItem') ?? []) {
      const key = row.dataset.virtualKey;
      if (!key) continue;
      const height = row.getBoundingClientRect().height;
      if (height <= 0 || Math.abs((this.measuredVirtualHeights.get(key) ?? 0) - height) <= 0.5) continue;
      this.measuredVirtualHeights.set(key, height);
      changed = true;
    }
    if (!changed) {
      this.measurementAnchor = undefined;
      return;
    }
    this.recomputeVirtualOffsets();
    if (anchor) this.pendingVirtualAnchor = anchor;
    else this.pendingVirtualRepin = true;
    this.requestUpdate();
    if (this.measurementAnchor) this.scheduleVirtualMeasurements();
  }

  private captureVirtualAnchor(containerArg: HTMLElement): { key: string; offset: number } | undefined {
    const containerTop = containerArg.getBoundingClientRect().top;
    const row = Array.from(this.shadowRoot?.querySelectorAll<HTMLElement>('.virtualItem') ?? [])
      .find((candidate) => candidate.getBoundingClientRect().bottom > containerTop);
    const key = row?.dataset.virtualKey;
    if (row && key) return { key, offset: containerTop - row.getBoundingClientRect().top };
    const localTop = this.virtualLocalScrollTop(containerArg);
    return this.captureVirtualOffsetAnchorAt(localTop);
  }

  private captureVirtualMetricAnchor(
    containerArg: HTMLElement,
  ): { key: string; offset: number } | undefined {
    const localTop = this.virtualLocalScrollTop(containerArg);
    for (const row of this.shadowRoot?.querySelectorAll<HTMLElement>('.virtualItem') ?? []) {
      const key = row.dataset.virtualKey;
      const index = key ? this.timelineIndexByKey.get(key) : undefined;
      if (index === undefined) continue;
      const item = this.timelineRenderItems[index]!;
      const offset = this.virtualOffsets[index] ?? 0;
      const height = this.measuredVirtualHeights.get(item.key) ?? this.estimatedVirtualHeight(item);
      if (offset + height > localTop) return { key: item.key, offset: localTop - offset };
    }
    return this.captureVirtualOffsetAnchorAt(localTop);
  }

  private captureVirtualOffsetAnchorAt(localTopArg: number): { key: string; offset: number } | undefined {
    let index = this.virtualIndexAtOffset(localTopArg);
    const item = this.timelineRenderItems[index];
    const itemHeight = item
      ? (this.measuredVirtualHeights.get(item.key) ?? this.estimatedVirtualHeight(item))
      : 0;
    if (
      item
      && localTopArg > (this.virtualOffsets[index] ?? 0) + itemHeight
      && index + 1 < this.timelineRenderItems.length
    ) index += 1;
    const anchorItem = this.timelineRenderItems[index];
    return anchorItem
      ? { key: anchorItem.key, offset: localTopArg - (this.virtualOffsets[index] ?? 0) }
      : undefined;
  }

  private restoreVirtualAnchor(
    containerArg: HTMLElement,
    anchorArg: { key: string; offset: number },
  ): void {
    const index = this.timelineIndexByKey.get(anchorArg.key);
    if (index === undefined) return;
    const stack = this.shadowRoot?.querySelector<HTMLElement>('.virtualStack');
    this.setProgrammaticScrollTop(
      containerArg,
      (stack?.offsetTop ?? 0) + (this.virtualOffsets[index] ?? 0) + anchorArg.offset,
    );
  }

  private restoreFullAnchor(
    containerArg: HTMLElement,
    anchorArg: { key: string; offset: number },
  ): void {
    const element = this.shadowRoot?.querySelector<HTMLElement>(
      `.stack > [data-timeline-key="${CSS.escape(anchorArg.key)}"]`,
    );
    if (!element) return;
    this.setProgrammaticScrollTop(
      containerArg,
      containerArg.scrollTop
        + element.getBoundingClientRect().top
        - containerArg.getBoundingClientRect().top
        + anchorArg.offset,
    );
  }

  private scheduleFullAnchorRestore(
    containerArg: HTMLElement,
    anchorArg: { key: string; offset: number },
  ): void {
    this.cancelFullAnchorRestore();
    const generation = this.fullAnchorRestoreGeneration;
    this.activeFullAnchor = anchorArg;
    const restore = (): boolean => {
      if (
        generation !== this.fullAnchorRestoreGeneration
        || !this.isConnected
        || this.scrollContainer !== containerArg
      ) return false;
      this.restoreFullAnchor(containerArg, anchorArg);
      return true;
    };
    restore();
    queueMicrotask(() => {
      if (!restore()) return;
      this.fullAnchorRestoreFrame = requestAnimationFrame(() => {
        this.fullAnchorRestoreFrame = null;
        if (!restore()) return;
        this.fullAnchorRestoreSecondFrame = requestAnimationFrame(() => {
          this.fullAnchorRestoreSecondFrame = null;
          if (!restore()) return;
          this.activeFullAnchor = undefined;
        });
      });
    });
  }

  private cancelFullAnchorRestore(): void {
    this.fullAnchorRestoreGeneration += 1;
    if (this.fullAnchorRestoreFrame !== null) cancelAnimationFrame(this.fullAnchorRestoreFrame);
    if (this.fullAnchorRestoreSecondFrame !== null) cancelAnimationFrame(this.fullAnchorRestoreSecondFrame);
    this.fullAnchorRestoreFrame = null;
    this.fullAnchorRestoreSecondFrame = null;
    this.activeFullAnchor = undefined;
  }

  private virtualLocalScrollTop(containerArg: HTMLElement): number {
    const stack = this.shadowRoot?.querySelector<HTMLElement>('.virtualStack');
    return Math.max(0, containerArg.scrollTop - (stack?.offsetTop ?? 0));
  }

  private virtualIndexAtOffset(offsetArg: number, offsetsArg = this.virtualOffsets): number {
    if (offsetsArg.length === 0) return 0;
    let low = 0;
    let high = offsetsArg.length - 1;
    while (low < high) {
      const middle = Math.floor((low + high + 1) / 2);
      if ((offsetsArg[middle] ?? 0) <= offsetArg) low = middle;
      else high = middle - 1;
    }
    return low;
  }

  private scheduleVirtualRangeUpdate(force = false): void {
    if (!this.virtualizationActive) return;
    if (force && this.virtualRangeFrame !== null) {
      cancelAnimationFrame(this.virtualRangeFrame);
      this.virtualRangeFrame = null;
    }
    if (this.virtualRangeFrame !== null) return;
    this.virtualRangeFrame = requestAnimationFrame(() => {
      this.virtualRangeFrame = null;
      this.updateVirtualRange();
    });
  }

  private updateVirtualRange(): void {
    const container = this.scrollContainer;
    if (!container || !this.virtualizationActive) return;
    const localTop = this.virtualLocalScrollTop(container);
    const startTarget = Math.max(0, localTop - virtualOverscanPx);
    const endTarget = localTop + container.clientHeight + virtualOverscanPx;
    const start = Math.max(0, this.virtualIndexAtOffset(startTarget) - 1);
    const end = Math.min(this.timelineRenderItems.length, this.virtualIndexAtOffset(endTarget) + 2);
    const retainedKeys = new Set(this.activeVirtualKeys());
    if (
      start === this.virtualStartIndex
      && end === this.virtualEndIndex
      && retainedKeys.size === this.retainedVirtualKeys.size
      && [...retainedKeys].every((key) => this.retainedVirtualKeys.has(key))
    ) return;
    this.virtualStartIndex = start;
    this.virtualEndIndex = end;
    this.retainedVirtualKeys = retainedKeys;
    if (this.activeSelectionKeys().length > 0) this.connectSelectionChangeListener();
    else if (!this.virtualizationEnablePending && !this.virtualizationDisablePending) {
      this.disconnectSelectionChangeListener();
    }
    this.requestUpdate();
  }

  private activeVirtualKeys(): string[] {
    const focusKey = this.activeFocusKey();
    return [
      ...(this.explicitRetainedVirtualKey ? [this.explicitRetainedVirtualKey] : []),
      ...(focusKey ? [focusKey] : []),
      ...this.activeSelectionKeys(),
    ].filter((key, index, keys) => keys.indexOf(key) === index);
  }

  private activeTimelineKey(): string | undefined {
    if (this.explicitRetainedVirtualKey) return this.explicitRetainedVirtualKey;
    return this.activeFocusKey() ?? this.activeSelectionKeys()[0];
  }

  private activeFocusKey(): string | undefined {
    const activeElement = this.shadowRoot?.activeElement;
    if (activeElement instanceof Element) {
      const focusedKey = this.ownedTimelineElement(activeElement)?.dataset.timelineKey
        ?? this.ownedVirtualRow(activeElement)?.dataset.virtualKey;
      if (focusedKey) return focusedKey;
    }
    return undefined;
  }

  private activeSelectionKeys(): string[] {
    if (this.selectionGestureActive) return [...this.selectionGestureKeys];
    const selection = document.getSelection();
    if (!selection || selection.rangeCount === 0) return [];
    if (selection.isCollapsed) {
      return this.hasCrossShadowSelectionFallback ? [...this.selectionGestureKeys] : [];
    }
    const range = selection.getRangeAt(0);
    const nodes: Array<Node | null> = [
      selection.anchorNode,
      selection.focusNode,
      range.startContainer,
      range.endContainer,
    ];
    if ('getComposedRanges' in selection && typeof selection.getComposedRanges === 'function') {
      const composedRanges = selection.getComposedRanges({
        shadowRoots: this.mountedShadowRoots(),
      });
      const composedRange = composedRanges[0];
      if (composedRange) nodes.push(composedRange.startContainer, composedRange.endContainer);
    }
    return [
      ...nodes
      .filter((node): node is Node => node !== null)
      .map((node) => this.timelineKeyForNode(node))
      .filter((key): key is string => key !== undefined),
      ...this.selectionGestureKeys,
    ]
      .filter((key, index, keys) => keys.indexOf(key) === index);
  }

  private mountedShadowRoots(): ShadowRoot[] {
    const roots: ShadowRoot[] = [];
    const collect = (rootArg: ShadowRoot): void => {
      for (const element of rootArg.querySelectorAll<HTMLElement>('*')) {
        if (!element.shadowRoot) continue;
        roots.push(element.shadowRoot);
        collect(element.shadowRoot);
      }
    };
    if (this.shadowRoot) collect(this.shadowRoot);
    return roots;
  }

  private timelineKeyForNode(nodeArg: Node): string | undefined {
    let node: Node | null = nodeArg;
    while (node) {
      if (node instanceof HTMLElement && node.classList.contains('virtualItem')) {
        const ownedRow = this.ownedVirtualRow(node);
        if (ownedRow) return ownedRow.dataset.virtualKey;
      }
      if (node instanceof HTMLElement) {
        const timelineElement = this.ownedTimelineElement(node);
        if (timelineElement) return timelineElement.dataset.timelineKey;
      }
      const root = node.getRootNode();
      node = node.parentNode ?? (root instanceof ShadowRoot ? root.host : null);
    }
    return undefined;
  }

  private timelineKeyForEvent(eventArg: Event): string | undefined {
    for (const candidate of eventArg.composedPath()) {
      if (!(candidate instanceof Element)) continue;
      const rowKey = this.ownedVirtualRow(candidate)?.dataset.virtualKey;
      if (rowKey) return rowKey;
      const timelineKey = this.ownedTimelineElement(candidate)?.dataset.timelineKey;
      if (timelineKey) return timelineKey;
    }
    return undefined;
  }

  private selectionOwnerForEvent(eventArg: Event): Element | undefined {
    for (const candidate of eventArg.composedPath()) {
      if (!(candidate instanceof HTMLElement)) continue;
      if (
        (
          candidate.tagName === 'DEES-HARNESS-MESSAGE'
          || candidate.tagName === 'DEES-HARNESS-PERMISSION-CARD'
          || candidate.tagName === 'DEES-HARNESS-QUESTION-CARD'
        )
        && this.timelineKeyForNode(candidate)
      ) return candidate;
    }
    return eventArg.composedPath()
      .filter((candidate): candidate is Element => candidate instanceof Element)
      .map((candidate) => this.ownedTimelineElement(candidate) ?? this.ownedVirtualRow(candidate))
      .find((candidate): candidate is HTMLElement => candidate !== undefined);
  }

  private get hasCrossShadowSelectionFallback(): boolean {
    return this.selectionGestureKeys.size > 1 || this.selectionGestureCrossesOwners;
  }

  private captureSelectionSnapshot(selectionArg: Selection | null): typeof this.selectionGestureSnapshot {
    if (!selectionArg) return undefined;
    return {
      anchorNode: selectionArg.anchorNode,
      anchorOffset: selectionArg.anchorOffset,
      focusNode: selectionArg.focusNode,
      focusOffset: selectionArg.focusOffset,
      rangeCount: selectionArg.rangeCount,
    };
  }

  private selectionMatchesGestureSnapshot(selectionArg: Selection): boolean {
    const snapshot = this.selectionGestureSnapshot;
    return Boolean(
      snapshot
      && snapshot.anchorNode === selectionArg.anchorNode
      && snapshot.anchorOffset === selectionArg.anchorOffset
      && snapshot.focusNode === selectionArg.focusNode
      && snapshot.focusOffset === selectionArg.focusOffset
      && snapshot.rangeCount === selectionArg.rangeCount,
    );
  }

  private clearSelectionGesture(): void {
    this.selectionGestureKeys.clear();
    this.selectionGestureActive = false;
    this.selectionGestureStartOwner = undefined;
    this.selectionGestureCrossesOwners = false;
    this.selectionGestureSnapshot = undefined;
  }

  /**
   * A row's disclosure is its own state, and the group around it carries the measure, so the
   * timeline re-renders to pick the new width up from the retained view state.
   */
  private readonly handleToolDisclosure = (): void => {
    this.requestUpdate();
  };

  private readonly handleCardRetention = (eventArg: CustomEvent<{ active?: boolean }>): void => {
    const pathElements = eventArg.composedPath()
      .filter((candidate): candidate is HTMLElement => candidate instanceof HTMLElement);
    const row = pathElements.map((candidate) => this.ownedVirtualRow(candidate))
      .find((candidate): candidate is HTMLElement => candidate !== undefined);
    const timelineElement = pathElements.map((candidate) => this.ownedTimelineElement(candidate))
      .find((candidate): candidate is HTMLElement => candidate !== undefined);
    const key = row?.dataset.virtualKey ?? timelineElement?.dataset.timelineKey;
    if (eventArg.detail?.active === true && key) this.explicitRetainedVirtualKey = key;
    else if (!key || this.explicitRetainedVirtualKey === key) this.explicitRetainedVirtualKey = undefined;
    this.retainedVirtualKeys = new Set(this.activeVirtualKeys());
    this.scheduleVirtualRangeUpdate();
    this.schedulePendingVirtualizationActivation();
  };

  private ownedVirtualRow(elementArg: Element): HTMLElement | undefined {
    const row = elementArg.closest<HTMLElement>('.virtualItem');
    return row?.getRootNode() === this.shadowRoot ? row : undefined;
  }

  private ownedTimelineElement(elementArg: Element): HTMLElement | undefined {
    const timelineElement = elementArg.closest<HTMLElement>('[data-timeline-key]');
    return timelineElement?.getRootNode() === this.shadowRoot ? timelineElement : undefined;
  }

  private disconnectVirtualObservers(): void {
    this.virtualRowResizeObserver?.disconnect();
    this.virtualViewportResizeObserver?.disconnect();
  }

  private repinAfterContentGrowth(): void {
    const content = this.observedScrollContent;
    if (!content) return;
    this.handleContentBlockSize(content.getBoundingClientRect().height);
  }

  private handleContentBlockSize(blockSizeArg: number): void {
    const previousBlockSize = this.observedContentBlockSize;
    this.observedContentBlockSize = blockSizeArg;
    // Disclosure collapse and window eviction are not new streamed content.
    // Let the browser handle shrink instead of fighting it with repins.
    if (previousBlockSize !== null && blockSizeArg > previousBlockSize + 0.5) {
      this.repinToBottom();
    }
    this.syncScrollEdgeFades();
  }

  private syncScrollEdgeFades(): void {
    const container = this.scrollContainer;
    const fadeTop = Boolean(this.fadeScrollEdges && container && container.scrollTop > 1);
    const fadeBottom = Boolean(
      this.fadeScrollEdges
      && container
      && container.scrollHeight - container.scrollTop - container.clientHeight > 1,
    );
    if (this.scrollFadeTop !== fadeTop) this.scrollFadeTop = fadeTop;
    if (this.scrollFadeBottom !== fadeBottom) this.scrollFadeBottom = fadeBottom;
  }

  private disconnectContentResizeObserver(): void {
    this.contentResizeObserver?.disconnect();
    this.contentResizeObserver = null;
    this.observedScrollContent = null;
    this.observedContentBlockSize = null;
  }

  public async disconnectedCallback(): Promise<void> {
    this.disconnectSelectionChangeListener();
    this.cancelFullAnchorRestore();
    if (this.followFrame !== null) cancelAnimationFrame(this.followFrame);
    if (this.followSecondFrame !== null) cancelAnimationFrame(this.followSecondFrame);
    if (this.virtualMeasureFrame !== null) cancelAnimationFrame(this.virtualMeasureFrame);
    if (this.virtualRangeFrame !== null) cancelAnimationFrame(this.virtualRangeFrame);
    if (this.virtualizationActivationFrame !== null) cancelAnimationFrame(this.virtualizationActivationFrame);
    this.followFrame = null;
    this.followSecondFrame = null;
    this.virtualMeasureFrame = null;
    this.virtualRangeFrame = null;
    this.virtualizationActivationFrame = null;
    this.pendingVirtualAnchor = undefined;
    this.pendingFullAnchor = undefined;
    this.measurementAnchor = undefined;
    this.pendingVirtualRepin = false;
    this.explicitRetainedVirtualKey = undefined;
    this.retainedVirtualKeys.clear();
    // An unmounted row owes no entrance: it arrives again as an ordinary row.
    this.enteringKeys.clear();
    this.clearSelectionGesture();
    this.resetLoadEarlierState();
    this.clearProgrammaticScrollMarker();
    this.disconnectContentResizeObserver();
    this.disconnectVirtualObservers();
    this.virtualRowResizeObserver = null;
    this.virtualViewportResizeObserver = null;
    await super.disconnectedCallback();
  }
}
